// Port "Mirrors" Teil B — planare Spiegel (THREE.Reflector). Aus Jans Renderer.
//
// HERKUNFT
//
// pro-editor/src/storyforger/Renderer/RendererWebGL.ts — updateMirrors +
// teardownMirror (~200 Z.), faithful `this.` -> `host.`.
//
// WAS ES TUT
//
// Fuer jedes `plane`-Primitive-Layer mit content.planarMirror legt es einen
// THREE.Reflector auf die Ebene, der die ECHTE Szene ueber die Flaeche spiegelt
// (korrekter Flachspiegel, nicht die Cube-Map von Teil A). Der Reflector-Shader
// wird gepatcht: Blur (9-Tap-Mip-Sample) + Staerke (Mix zur Grundfarbe). Die
// Basis-Ebene wird versteckt, solange der Spiegel aktiv ist.
//
// VORAUSSETZUNG: das `plane`-Primitive (Primitive-Port) — der Reflector teilt
// sich dessen Geometrie + Transform (group.getObjectByName('plane')).
//
// ADAPTIONEN: keine. Der Reflector-Shader in three 0.183 enthaelt exakt die
// Strings, die Jan patcht (verifiziert) — der Patch greift, statt nur
// gnaedig zu degradieren.
//
// ADDITIV-SICHERHEIT: ohne gesetztes planarMirror-Flag ist mirrorLayers leer ->
// updateMirrors baut nichts. Bestand unveraendert.
//
// VERIFIKATION: visuell (Devview /__dev__/planar-mirror).
import THREE, { Reflector } from "../utils/THREE";
import { colorToThree } from "../utils/color/threeColor";
import { istMobilesGeraet } from "../utils/geraet";
import { SpiegelWeichzeichner, weichSigmaBezug } from "./spiegel-weichzeichner";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TODOAny = any;

/**
 * Groesse + Glaettung der Spiegel-Textur (Jans iPhone-Fund 19.09.2026, „da
 * sind Pixel zu sehen, die flackern").
 *
 * Vorher: immer QUADRATISCH (1024², mobil hoechstens 512²) und ohne
 * Multisampling. Die Spiegel-Textur wird aber bildschirm-projektiv abgetastet —
 * sie liegt 1:1 ueber dem Bild. Ein 512²-Quadrat ueber einem Hochkant-Handy
 * (~780 × 1690 Geraete-Pixel) wird senkrecht 3,3-fach vergroessert, und weil
 * der Spiegel-Render selbst keine Kantenglaettung hatte, wandern seine
 * Treppenstufen bei jeder Kamerabewegung: genau das Flackern.
 *
 * Jetzt:
 *  - Seitenverhaeltnis = das der Zeichenflaeche (kein verschwendetes Quadrat),
 *    lange Seite nach Unschaerfe gedeckelt wie bisher (1024 / 512 / 256 / 128).
 *  - SCHARFER Spiegel (blur 0): 4-fach Multisampling im Spiegel-Render. Das
 *    ging vorher nicht, weil MSAA-Ziele keine Mipmaps tragen — die braucht aber
 *    nur der UNSCHARFE Spiegel (LOD-Abtastung). Scharf wird nie verkleinert
 *    abgetastet (Textur ≤ Bild), Mipmaps sind dort nutzlos.
 *  - Mobil: scharf bis 896 lange Seite (Hochkant ~896 × 414 = 1,4 × die Pixel
 *    des alten 512²-Quadrats), unscharf weiter hoechstens 512 — durch das
 *    Seitenverhaeltnis sogar billiger als vorher (iPhone-Speicherdeckel).
 *  - Auf 32 px gerastert, damit ein Fenster-Ziehen nicht staendig neu baut.
 */
export interface SpiegelZiel {
    breite: number;
    hoehe: number;
    /** lange Seite — Bezugsgroesse fuer die Unschaerfe-Stufen */
    res: number;
    /** Multisampling-Stufen des Spiegel-Renders */
    msaa: number;
    /** unscharf: das Bild geht durch den Gauss-Weichzeichner (spiegel-weichzeichner.ts) */
    weich: boolean;
}

export function spiegelZiel(
    blur: number,
    bildBreite: number,
    bildHoehe: number,
    mobil: boolean,
    /** leichte Szene (s. szeneIstLeicht) — darf am Handy in voller Aufloesung spiegeln */
    leicht = false,
): SpiegelZiel {
    const b = Math.max(0, Math.min(1, blur || 0));
    const scharf = b < 1e-4;
    // Der Deckel ist ein PIXEL-BUDGET: die Seitenlaenge eines gleich grossen
    // Quadrats. (Erste Fassung vom 19.09. deckelte die LANGE Seite — im
    // Hochformat wurde ein unscharfer Spiegel dadurch nur 224 px breit statt
    // wie frueher 512: sichtbare Kloetzchen, Jans zweites iPhone-Foto.)
    // KEINE Unschaerfe-Stufen mehr (Jans Fund 21.09.2026): frueher schrumpfte die
    // Textur bei 25/50/70 % auf 512/256/128 — ein winziger Glanzpunkt wird bei
    // halber Aufloesung anders erfasst (Schattierungs-Aliasing), die Spiegelung
    // des Lichts SPRANG beim Schritt 24 → 25 sichtbar. Jetzt rendert jede
    // Unschaerfe mit demselben Budget; weich macht allein der Gauss-
    // Weichzeichner (spiegel-weichzeichner.ts).
    // Die Kosten deckelt weiter das Handy-Budget unten.
    // VOLLE BILDAUFLOESUNG am Desktop, scharf wie unscharf (Jans Sichttest
    // 21.09.2026 abends, drei Stufen nebeneinander: 1024 flackert, 2048 „viel
    // besser"): die glaenzende Balkenkante ist in einem kleiner gerechneten
    // Spiegelbild duenner als ein Bildpunkt — mal getroffen, mal nicht; das
    // Weichzeichnen verschmiert die Treffer zu Knubbeln, die beim Scrollen
    // wandern. Nie groesser als das Bild (s. faktor unten).
    let deckel = 2048;
    if (mobil) {
        // Handy: schwere Szenen bleiben vorsichtig (iPhone-Speicherdeckel
        // 26.08.2026); LEICHTE Szenen duerfen bis zur vollen Bildaufloesung —
        // dort kostet der Spiegel wenig, und die halbe Aufloesung war genau
        // das, was man als grobe, wandernde Pixel sah.
        const mobilDeckel = leicht ? (scharf ? 1280 : 1024) : scharf ? 640 : 512;
        deckel = Math.min(deckel, mobilDeckel);
    }

    const w = Math.max(1, bildBreite || 0);
    const h = Math.max(1, bildHoehe || 0);
    // gleiche Flaeche wie deckel², Form des Bildes, nie groesser als das Bild
    const faktor = Math.min(1, deckel / Math.sqrt(w * h));
    const raster = (v: number) => Math.max(32, Math.round((v * faktor) / 32) * 32);
    const breite = raster(w);
    const hoehe = raster(h);
    return {
        breite,
        hoehe,
        // Bezugsgroesse der Unschaerfe-Stufen: Seite des flaechengleichen Quadrats
        res: Math.round(Math.sqrt(breite * hoehe)),
        // MSAA IMMER (Jans Fund 21.09.2026, „der weichgezeichnete Spiegel flackert
        // in der Animation"): der unscharfe Spiegel rendert bisher OHNE Kanten-
        // glaettung — harte Treppen und Glanzpunkte wandern beim Scrollen von
        // Texel zu Texel, das Weichzeichnen macht daraus ein Flackern. Die alte
        // Begruendung („ein MSAA-Ziel traegt keine Mipmaps") stimmt fuer three
        // 0.183 nicht: nach dem MSAA-Resolve erzeugt three die Mipmaps
        // (updateMultisampleRenderTarget → updateRenderTargetMipmap), genauso
        // arbeitet sein eigenes Transmissions-Ziel.
        msaa: 4,
        weich: !scharf,
    };
}

/**
 * AUSLAUF (Jan 20.09.2026, „nicht wirklich realistisch und schoen"): Ein echter
 * Glanzboden spiegelt am Fuss eines Objekts klar und wird mit der Hoehe des
 * gespiegelten Punkts blasser und weicher. Bisher war die Spiegelung eine
 * gleichmaessig helle, hart endende Kopie.
 *
 * `fade` 0..1 → Laenge in Welt-Einheiten, nach der die Spiegelung auf ~10 %
 * gefallen ist: 0 = aus; 0,5 (Vorgabe) ≈ 40 % der Bildhoehe; 1 ≈ 8 %.
 *
 * STUFENLOS AB NULL (Jans Fund 21.09.2026, „Slider verhaelt sich komisch"):
 * die erste Fassung sprang von 0 (aus) bei 1 % direkt auf 72 % der Bildhoehe.
 * Stehen die Objekte AUF dem Spiegel, faellt das kaum auf; schweben sie weit
 * davor (Statue/Donut vor einer Spiegelwand), war schon bei 1 % fast alles
 * blass. Jetzt: ab `SPIEGEL_AUSLAUF_KNICK` (0,25) aufwaerts unveraendert linear
 * — gespeicherte Werte sehen gleich aus —, darunter waechst die Laenge
 * quadratisch gegen unendlich (0,25 → 56 %, 0,1 → 3,5-fache, 0,01 → 350-fache
 * Bildhoehe = praktisch aus). 0 selbst bleibt „aus".
 */
export const SPIEGEL_AUSLAUF_VORGABE = 0.5;
export const SPIEGEL_AUSLAUF_KNICK = 0.25;
export function spiegelAuslaufLaenge(fade: number | undefined, bildHoeheWelt: number): number {
    const f = Math.max(0, Math.min(1, fade ?? SPIEGEL_AUSLAUF_VORGABE));
    if (f < 1e-4) return 0;
    const linear = (x: number) => 0.72 - 0.64 * x; // 0,25 → 56 %, 0,5 → 40 %, 1 → 8 %
    const anteil =
        f >= SPIEGEL_AUSLAUF_KNICK
            ? linear(f)
            : linear(SPIEGEL_AUSLAUF_KNICK) * (SPIEGEL_AUSLAUF_KNICK / f) ** 2;
    return Math.max(1, (bildHoeheWelt || 700) * anteil);
}

/**
 * BLICKWINKEL (Fresnel), Jans „Variante 3" (20.09.2026): ein polierter Boden
 * spiegelt flach gesehen stark, steil von oben kaum. 0 = perfekter Spiegel aus
 * jedem Winkel (das alte Verhalten); fehlt der Wert im Dokument, gilt 1.
 */
export const SPIEGEL_BLICKWINKEL_VORGABE = 1;
export function spiegelBlickwinkel(fresnel: number | undefined): number {
    const f = fresnel ?? SPIEGEL_BLICKWINKEL_VORGABE;
    return Number.isFinite(f) ? Math.max(0, Math.min(1, f)) : SPIEGEL_BLICKWINKEL_VORGABE;
}

/** Tap-Schritt (UV, x-Achse) der Nachglaettung des scharfen Spiegels: 0,5 Texel
 *  (der Kern reicht bis zum doppelten Schritt → ~1 Texel Radius). */
export function spiegelNachglaettung(texturBreite: number): number {
    return 0.5 / Math.max(32, texturBreite || 0);
}

/**
 * Ist die Szene LEICHT genug, um am Handy in voller Aufloesung zu spiegeln?
 * Der Spiegel rendert die ganze Szene ein zweites Mal. Schwer heisst: viel
 * Geometrie (grosse GLB-Modelle) oder Glas — Transmission legt selbst ein
 * bildschirmgrosses Zwischenziel an, das war die Absturz-Szene vom 26.08.
 * Reine Entscheidung aus zwei Messwerten, damit sie testbar bleibt.
 */
export function szeneIstLeicht(dreieckeProBild: number, hatTransmission: boolean): boolean {
    // 1,5 Mio.: Jans Bar-Chart liegt bei 720.000 (fein unterteilte Spiegel-
    // Ebene, doppelt durch den Spiegel-Durchlauf) und laeuft am iPhone fluessig.
    // Dreiecke sind fuer Handy-GPUs billig; teuer und absturztraechtig sind
    // bildschirmgrosse Zwischenziele — also Glas.
    return !hatTransmission && dreieckeProBild > 0 && dreieckeProBild < 1500000;
}

/**
 * Soll der Spiegel am Handy gedrosselt laufen? Mit Hysterese, damit er nicht
 * flattert: die Drossel SENKT die Bildzeit — ohne Abstand zwischen den beiden
 * Schwellen wuerde er staendig hin- und herschalten (und genau dann wackeln).
 * Rein bei > 26 ms (unter ~38 fps), raus erst bei < 18 ms (ueber ~55 fps).
 */
export function spiegelGedrosselt(bisher: boolean, bildzeitMs: number): boolean {
    if (bisher) return bildzeitMs >= 18;
    return bildzeitMs > 26;
}

/** Mindestabstand zweier Spiegel-Renderings: 8 ms = jedes Bild, 30 ms = ~33 fps. */
export function spiegelAbstand(mobil: boolean, gedrosselt: boolean): number {
    return mobil && gedrosselt ? 30 : 8;
}

/**
 * Ist jetzt eine neue Spiegel-Aufnahme faellig?
 *  - gedrosseltes Handy: weiter nach ZEIT (~33 fps), das ist der Sinn der Drossel;
 *  - sonst EINMAL JE BILD (Bild-Nummer des Renderers) — nie zweimal im selben
 *    Bild, aber auch nie ein Bild auslassen, egal wie schnell der Bildschirm ist;
 *  - ohne Bild-Nummer (fremder Host): die fruehere 8-ms-Sperre.
 */
export function spiegelFaellig(p: {
    bildNr: number | undefined;
    letztesBild: number | undefined;
    seitLetztemMs: number;
    mobil: boolean;
    gedrosselt: boolean;
}): boolean {
    if (p.mobil && p.gedrosselt) return p.seitLetztemMs >= spiegelAbstand(true, true);
    if (p.bildNr === undefined) return p.seitLetztemMs >= spiegelAbstand(p.mobil, false);
    return p.bildNr !== p.letztesBild;
}

export interface MirrorParams {
    color?: TODOAny;
    mixColor?: TODOAny;
    blur: number;
    strength: number;
    /** Auslauf 0..1 (0 = aus): Spiegelung wird mit der Hoehe ueber dem Boden blasser + weicher. */
    fade?: number;
    /** Blickwinkel 0..1 (Fresnel): flach gesehen stark, steil von oben schwach; 0 = aus. */
    fresnel?: number;
}

export interface MirrorRig {
    reflector: TODOAny;
    group: THREE.Object3D;
    mesh: THREE.Mesh;
    res: number;
    /** Texturbreite in px — Bezug fuer die Nachglaettung */
    breite: number;
    /** "BxH:msaa" — aendert sich das Ziel, wird der Spiegel neu gebaut */
    zielKey: string;
    /** last time the reflection was actually re-rendered (throttle, ms) */
    lastRender: number;
    /** Bild-Nummer der letzten Spiegel-Aufnahme (s. MirrorHost.spiegelBildNr) */
    letztesBild?: number;
    /** Gauss-Weichzeichner (nur unscharf) + seine Breite in Textur-Pixeln */
    weich?: SpiegelWeichzeichner;
    sigmaPx?: number;
}

/**
 * Der Teil des Renderers, den die planaren Spiegel anfassen.
 */
export interface MirrorHost {
    webGLRenderer: THREE.WebGLRenderer | undefined;
    webGlScene: THREE.Scene | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layerMeshes: { [id: string]: any };
    mirrorLayers: { [id: string]: boolean };
    mirrorParams: { [id: string]: MirrorParams };
    mirrorRigs: { [id: string]: MirrorRig };
    /** Wohin die Spiegelung auslaeuft: die Hintergrundfarbe der Szene (0..1 je Kanal). */
    spiegelAuslaufZiel?: { r: number; g: number; b: number };
    /** Hoehe des sichtbaren Bildes in Welt-Einheiten auf Buehnentiefe — Massstab fuer die Auslauf-Laenge. */
    spiegelBildHoehe?: number;
    /**
     * Spiegel aussetzen: solange true, erneuert KEIN Spiegel sein Bild (das alte
     * bleibt stehen). Gesetzt waehrend des GPU-Aufwaerm-Bilds (uploadToGpu): dort
     * steht das frisch gebaute Objekt unskaliert im Szenen-Ursprung — der Spiegel
     * nahm dieses Bild auf, und weil das eigentliche Bild < 8 ms spaeter folgt
     * (Dedupe), blieb der Riese bis zum naechsten Bild im Spiegel stehen: beim
     * Ziehen eines Geometrie-Reglers blitzte eine helle Riesenform auf
     * (Jans Fund 21.09.2026).
     */
    spiegelAusgesetzt?: boolean;
    /**
     * Laufende Bild-Nummer des Renderers (zaehlt je animate hoch). Der Spiegel
     * erneuert sich EINMAL JE BILD. Vorher galt eine Zeit-Sperre von 8 ms (gegen
     * Mehrfach-Draws im selben Bild): auf 120-Hz-Bildschirmen dauert ein Bild
     * 8,3 ms — kam eines etwas frueher, uebersprang der Spiegel es und zeigte
     * ein Bild lang die alte Kamera; die Spiegelung hinkte unregelmaessig nach
     * (Jans Fund 21.09.2026, „der weichgezeichnete Spiegel flackert in der
     * Animation"). Fehlt die Nummer (fremder Host), gilt weiter die Zeit-Sperre.
     */
    spiegelBildNr?: number;
    /** Editor-Hilfszeichen (Zielpunkt-/Drehpunkt-Marker …), die NICHT gespiegelt werden duerfen. */
    spiegelEditorZeichen?: () => THREE.Object3D[];
}

// „Leichte Szene" je Renderer merken: die Dreiecke des letzten Bildes liefert
// three gratis, die Suche nach Glas-Materialien laeuft hoechstens alle 2 s.
// Einmal SCHWER bleibt schwer (kein Hin und Her der Aufloesung, kein Neubau-
// Flattern); LEICHT wird erst nach 1 s stabiler Messung vergeben.
const leichtZustand = new WeakMap<object, { leicht: boolean; schwerFix: boolean; seit: number; geprueft: number; glas: boolean }>();
export function leichtGemerkt(host: Pick<MirrorHost, "webGLRenderer" | "webGlScene">): boolean {
    const jetzt = typeof performance !== "undefined" ? performance.now() : Date.now();
    let z = leichtZustand.get(host);
    if (!z) {
        z = { leicht: false, schwerFix: false, seit: jetzt, geprueft: -1e9, glas: false };
        leichtZustand.set(host, z);
    }
    if (z.schwerFix) return false;
    if (jetzt - z.geprueft > 2000) {
        z.geprueft = jetzt;
        let glas = false;
        host.webGlScene?.traverse((o: TODOAny) => {
            if (glas || !o.isMesh) return;
            const mats = Array.isArray(o.material) ? o.material : [o.material];
            for (const m of mats) if (m && typeof m.transmission === "number" && m.transmission > 0) glas = true;
        });
        z.glas = glas;
    }
    // info.render.triangles zaehlt das ganze letzte Bild inkl. Spiegel-Durchlauf
    const dreiecke = (host.webGLRenderer as TODOAny)?.info?.render?.triangles ?? 0;
    const jetztLeicht = szeneIstLeicht(dreiecke, z.glas);
    if (!jetztLeicht && dreiecke > 0) {
        z.schwerFix = true;
        z.leicht = false;
        return false;
    }
    if (!z.leicht && jetztLeicht && jetzt - z.seit > 1000) z.leicht = true;
    return z.leicht;
}

// Planar mirror (THREE.Reflector): for each flat plane flagged planarMirror,
// add a Reflector that reflects the REAL scene across the plane. Managed here —
// created/torn down based on the flag; the reflection itself renders via the
// Reflector's own onBeforeRender during the main pass. The base plane mesh is
// hidden so only the mirror shows.
export function updateMirrors(host: MirrorHost) {
    if (!host.webGLRenderer || !host.webGlScene) return;

    // Zielgroesse + Glaettung der Spiegel-Textur: s. spiegelZiel().
    const puffer = host.webGLRenderer.getDrawingBufferSize(new THREE.Vector2());
    const mobilGeraet = istMobilesGeraet();
    const leicht = mobilGeraet ? leichtGemerkt(host) : false;
    const zielFuer = (blur: number) => spiegelZiel(blur, puffer.x, puffer.y, mobilGeraet, leicht);
    const keyVon = (z: SpiegelZiel) => `${z.breite}x${z.hoehe}:${z.msaa}:${z.weich ? "w" : "s"}`;

    // tear down rigs no longer needed (flag off, object rebuilt, or the blur
    // resolution bucket changed — the target size is fixed at construction)
    for (const id of Object.keys(host.mirrorRigs)) {
        const rig = host.mirrorRigs[id];
        const group = host.layerMeshes[id] as THREE.Object3D | undefined;
        const wantKey = keyVon(zielFuer(host.mirrorParams[id]?.blur ?? 0));
        if (!host.mirrorLayers[id] || !group || rig.group !== group || rig.zielKey !== wantKey) {
            teardownMirror(host, id);
        }
    }

    // create a rig for each newly-active mirror plane
    for (const id of Object.keys(host.mirrorLayers)) {
        if (!host.mirrorLayers[id] || host.mirrorRigs[id]) continue;
        const group = host.layerMeshes[id] as THREE.Object3D | undefined;
        if (!group) continue;
        const mesh = group.getObjectByName("plane") as THREE.Mesh | undefined;
        if (!mesh || !mesh.geometry) continue;
        // share the plane's geometry + local transform so the mirror sits exactly
        // on the plane and reflects across the same (possibly rotated) surface.
        const ziel = zielFuer(host.mirrorParams[id]?.blur ?? 0);
        const res = ziel.res;
        const reflector = new Reflector(mesh.geometry, {
            color: 0x8a8a8a,
            textureWidth: ziel.breite,
            textureHeight: ziel.hoehe,
            // MSAA im Spiegel-Render — scharf UND unscharf (s. spiegelZiel):
            // glatte Kanten statt wandernder Treppen, kein Flackern beim Scrollen.
            multisample: ziel.msaa,
        });
        // kill the horizontal shimmer/stripes where the mirror is seen almost
        // edge-on: mipmap + anisotropic filtering on the reflection texture
        const reflRt = (reflector as TODOAny).getRenderTarget?.();
        if (reflRt?.texture) {
            // Keine Mipmaps: scharf wird nie verkleinert abgetastet, unscharf
            // laeuft durch den Gauss-Weichzeichner (der liest 1:1).
            // 8 BIT (sRGB) STATT HALBGLEITKOMMA (Jans Fund 21.09.2026 abends: die
            // hell glaenzenden Balkenkanten flackern im Spiegel, gestrichelte
            // Treppen). Im Gleitkomma-Ziel schreibt ein Glanzlicht Werte weit
            // ueber 1. Die Kantenglaettung mittelt danach: trifft die duenne Kante
            // nur 1 von 4 Messpunkten, bleibt von „20" immer noch „5" = volles
            // Weiss, trifft sie keinen, Schwarz — an/aus von Texel zu Texel und
            // von Bild zu Bild. Das HAUPTBILD hat das Problem nicht, weil sein
            // 8-Bit-Ziel jeden Wert VOR dem Mitteln bei 1 kappt. Genau so jetzt
            // der Spiegel; sRGB-Ablage, damit dunkle Szenen nicht streifig werden
            // (die Grafikkarte wandelt beim Schreiben/Lesen selbst um).
            reflRt.texture.type = THREE.UnsignedByteType;
            reflRt.texture.colorSpace = THREE.SRGBColorSpace;
            reflRt.texture.generateMipmaps = false;
            reflRt.texture.minFilter = THREE.LinearFilter;
            reflRt.texture.magFilter = THREE.LinearFilter;
            reflRt.texture.needsUpdate = true;
        }
        reflector.name = "planarMirror";
        // exactly on the base plane (same geometry + transform)
        reflector.position.copy(mesh.position);
        reflector.quaternion.copy(mesh.quaternion);
        reflector.scale.copy(mesh.scale);
        const rmat = reflector.material as TODOAny;
        rmat.polygonOffset = true;
        rmat.polygonOffsetFactor = -1;
        rmat.polygonOffsetUnits = -4;
        // "mirror strength" WITHOUT a coplanar base plane: patch the shader to blend
        // the reflection toward a base colour by uOpacity. replace-or-nothing.
        rmat.uniforms.uOpacity = { value: 1 };
        rmat.uniforms.uMixColor = { value: new THREE.Color(0.5, 0.5, 0.5) };
        rmat.uniforms.uBlurStep = { value: 0 };
        rmat.uniforms.uBlurForm = { value: new THREE.Vector2(1, ziel.breite / ziel.hoehe) };
        // Tiefe des Spiegel-Durchlaufs (fuer den Auslauf). Bei MSAA loest three
        // sie beim Blit mit auf (resolveDepthBuffer, r183).
        const tiefe = new THREE.DepthTexture(ziel.breite, ziel.hoehe);
        tiefe.type = THREE.UnsignedIntType;
        tiefe.minFilter = THREE.NearestFilter;
        tiefe.magFilter = THREE.NearestFilter;
        if (reflRt) reflRt.depthTexture = tiefe;
        rmat.uniforms.tSpiegelTiefe = { value: tiefe };
        rmat.uniforms.uSpiegelInvVP = { value: new THREE.Matrix4() };
        rmat.uniforms.uSpiegelEbene = { value: new THREE.Vector4(0, 1, 0, 0) };
        rmat.uniforms.uAuslauf = { value: 0 };
        rmat.uniforms.tWeich = { value: null };
        rmat.uniforms.uWeichAn = { value: 0 };
        rmat.uniforms.uSpiegelTexel = { value: new THREE.Vector2(1.5 / ziel.breite, 1.5 / ziel.hoehe) };
        rmat.uniforms.uAuslaufZiel = { value: new THREE.Vector3(0, 0, 0) };
        rmat.uniforms.uFresnel = { value: 0 };
        // Welt-Position der Bodenstelle fuer den Blickwinkel (Fresnel, s. unten)
        rmat.vertexShader = String(rmat.vertexShader)
            .replace("varying vec4 vUv;", "varying vec4 vUv;\n\t\tvarying vec3 vSpiegelWelt;")
            .replace(
                "vUv = textureMatrix * vec4( position, 1.0 );",
                "vUv = textureMatrix * vec4( position, 1.0 );\n\t\t\tvSpiegelWelt = ( modelMatrix * vec4( position, 1.0 ) ).xyz;"
            );
        rmat.fragmentShader = String(rmat.fragmentShader)
            .replace(
                "uniform sampler2D tDiffuse;",
                "uniform sampler2D tDiffuse;\n\t\tuniform float uOpacity;\n\t\tuniform vec3 uMixColor;\n\t\tuniform float uBlurStep;\n\t\tuniform vec2 uBlurForm;\n\t\tuniform sampler2D tSpiegelTiefe;\n\t\tuniform mat4 uSpiegelInvVP;\n\t\tuniform vec4 uSpiegelEbene;\n\t\tuniform float uAuslauf;\n\t\tuniform sampler2D tWeich;\n\t\tuniform float uWeichAn;\n\t\tuniform vec2 uSpiegelTexel;\n\t\tuniform vec3 uAuslaufZiel;\n\t\tuniform float uFresnel;\n\t\tvarying vec3 vSpiegelWelt;\n\t\tfloat spiegelFadeAn( vec2 uvS ) {\n\t\t\tif ( uAuslauf <= 0.0 ) return 1.0;\n\t\t\tfloat dS = texture( tSpiegelTiefe, uvS ).r;\n\t\t\tif ( dS >= 0.99999 ) return -1.0;\n\t\t\tvec4 wpS = uSpiegelInvVP * vec4( uvS * 2.0 - 1.0, dS * 2.0 - 1.0, 1.0 );\n\t\t\tfloat hS = abs( dot( uSpiegelEbene.xyz, wpS.xyz / wpS.w ) + uSpiegelEbene.w );\n\t\t\treturn exp( -2.3 * hS / uAuslauf );\n\t\t}"
            )
            // UNSCHAERFE = feste Mip-Stufe + 13 gewichtete Abtastungen.
            //
            // KORREKTUR 20.09.2026 (Jans Desktop-Bilder: Kamm-Streifen an den
            // Enden der Spiegelung, „egal ob scharf oder unscharf"): Bisher stand
            // hier `texture2D( tex, uv, uBlurLod )`. Das dritte Argument ist in
            // GLSL ein LOD-*BIAS* — er wird zur automatisch bestimmten Stufe
            // ADDIERT. Wird die Spiegel-Textur vergroessert abgetastet (Retina-
            // Desktop, iPhone: Textur kleiner als das Bild), ist die automatische
            // Stufe negativ und frisst den Bias auf: abgetastet wurde Stufe 0,
            // also das SCHARFE Bild — neunmal versetzt. Das ist der Kamm.
            // `textureLod` setzt die Stufe absolut, unabhaengig von der Groesse.
            //
            // Dazu ein runderer Kern: Mitte + 8 im Ring 1 + 4 im Ring 2,
            // gaussaehnlich gewichtet (vorher 9 gleich gewichtete = Kasten).
            .replace(
                "vec4 base = texture2DProj( tDiffuse, vUv );",
                [
                    "vec2 uvB = vUv.xy / vUv.w;",
                    // Tap-Schritt je Achse: uBlurForm macht ihn in PIXELN gleich gross
                    // (die Textur hat seit 19.09.2026 Bildform, nicht mehr Quadrat)
                    "vec2 sB = uBlurStep * uBlurForm;",
                    // AUSLAUF: Hoehe h des gespiegelten Punkts ueber der Spiegel-
                    // Ebene aus der Tiefe des Spiegel-Durchlaufs zurueckrechnen.
                    // BLICKWINKEL (Fresnel): ein Glanzboden spiegelt flach gesehen stark,
                    // steil von oben kaum. uFresnel 0 = aus (perfekter Spiegel wie bisher).
                    "float cosS = abs( dot( normalize( cameraPosition - vSpiegelWelt ), uSpiegelEbene.xyz ) );",
                    "float fresS = mix( 1.0, 0.12 + 0.88 * pow( 1.0 - cosS, 3.0 ), uFresnel );",
                    // AUSLAUF JE ABTASTUNG (20.09.2026, Jans „heller Umriss"): vorher
                    // dimmte EIN Wert aus der Tiefe am Pixel die schon verwischte Farbe.
                    // Knapp AUSSERHALB der Silhouette liegt dort keine Geometrie → Faktor 1,
                    // die hereingewischte Farbe blieb ungedimmt, innen war sie fast weg:
                    // ein heller Strich um die ausgeblendeten Enden. Jetzt:
                    //  1. jede der 13 Abtastungen traegt ihren EIGENEN Hoehen-Faktor;
                    //  2. Abtastungen OHNE Geometrie (-1) erben den kleinsten Faktor der
                    //     Nachbarschaft (Kern + 1 Texel) — das Verwischte um ein fast
                    //     ausgeblendetes Ende ist damit ebenfalls fast ausgeblendet; weit
                    //     weg von jedem Objekt bleibt der Faktor 1 (Himmel/HDRI unberuehrt).
                    // Schritt 2 ersetzt zugleich die fruehere 1-Texel-Weitung (MSAA-Fransen).
                    "const vec2 oS[13] = vec2[13]( vec2( 0.0 ), vec2( 1.0, 0.0 ), vec2( -1.0, 0.0 ), vec2( 0.0, 1.0 ), vec2( 0.0, -1.0 ), vec2( 0.7071 ), vec2( -0.7071 ), vec2( 0.7071, -0.7071 ), vec2( -0.7071, 0.7071 ), vec2( 2.0, 0.0 ), vec2( -2.0, 0.0 ), vec2( 0.0, 2.0 ), vec2( 0.0, -2.0 ) );",
                    "const float wS[13] = float[13]( 0.20, 0.085, 0.085, 0.085, 0.085, 0.065, 0.065, 0.065, 0.065, 0.05, 0.05, 0.05, 0.05 );",
                    "float fT[13];",
                    "float fMin = 1.0;",
                    "for ( int i = 0; i < 13; i ++ ) {",
                    "  fT[i] = spiegelFadeAn( uvB + oS[i] * sB );",
                    "  if ( fT[i] >= 0.0 ) fMin = min( fMin, fT[i] );",
                    "}",
                    "for ( int i = 1; i < 5; i ++ ) {",
                    "  float fN = spiegelFadeAn( uvB + oS[i] * uSpiegelTexel );",
                    "  if ( fN >= 0.0 ) fMin = min( fMin, fN );",
                    "}",
                    // Weicher mit der Hoehe nur ueber die Mip-Stufe (stetig gefiltert,
                    // franst nicht) — KEINE hoehenabhaengige Tap-Spreizung (s. Historie).
                    "vec4 base = vec4( 0.0 );",
                    "float fadeS = 0.0;",
                    "for ( int i = 0; i < 13; i ++ ) {",
                    "  float fI = fT[i] < 0.0 ? fMin : fT[i];",
                    "  base += textureLod( tDiffuse, uvB + oS[i] * sB, 0.0 ) * ( wS[i] * fI );",
                    "  fadeS += wS[i] * fI;",
                    "}",
                    // base ist schon gedimmt → Rest bis 1 aufs Auslauf-Ziel auffuellen
                    "base.rgb += uAuslaufZiel * ( 1.0 - fadeS );",
                    // UNSCHARF: fertiges Bild aus dem Gauss-Weichzeichner (Auslauf
                    // steckt premultipliziert drin, Alpha = Auslauf-Faktor).
                    "if ( uWeichAn > 0.5 ) {",
                    "  vec4 wB = texture( tWeich, uvB );",
                    "  base = vec4( wB.rgb + uAuslaufZiel * ( 1.0 - wB.a ), 1.0 );",
                    "}",
                ].join("\n\t\t\t")
            )
            .replace(
                "vec4( blendOverlay( base.rgb, color ), 1.0 )",
                "vec4( mix( uMixColor, mix( blendOverlay( uAuslaufZiel, color ), blendOverlay( base.rgb, color ), fresS ), uOpacity ), 1.0 )"
            );
        rmat.needsUpdate = true;
        (mesh.parent || group).add(reflector);

        // Dedupe statt Drossel (26.08.2026, Jans „ruckelt beim Animieren"):
        // Die 1.0-Drossel auf ~33 fps (>= 30 ms) liess den Spiegelinhalt bei
        // 60-fps-Animationen sichtbar jeden zweiten Frame springen. 8 ms
        // kollabiert weiterhin Mehrfach-Draws INNERHALB eines Frames (z. B.
        // den Gizmo-Pass) zu einem einzigen Reflexions-Render, aktualisiert
        // die Spiegelung aber in jedem Frame (bis 120 fps). Die adaptive
        // Aufloesung (spiegelZiel) bleibt der Kosten-Deckel.
        const rig: MirrorRig = { reflector, group, mesh, res, breite: ziel.breite, zielKey: keyVon(ziel), lastRender: 0 };
        if (ziel.weich) rig.weich = new SpiegelWeichzeichner(ziel.breite, ziel.hoehe);
        const origOnBeforeRender = reflector.onBeforeRender as TODOAny;
        // Mobil ~33 fps, Desktop jeden Frame (8-ms-Dedupe, #475). Der
        // STANDBILD-Versuch (#480: nach 1,25 s einfrieren) ist auf Jans
        // Sichttest hin ZURUECKGEBAUT — das eingefrorene Spiegelbild passte
        // beim Scrollen nicht mehr zur Szene; der Live-Spiegel mit 33 fps
        // war die bessere Balance (26.08.2026).
        //
        // MOBIL ADAPTIV (Jans iPhone-Fund 19.09.2026, „die Spiegelung wackelt
        // ganz schrecklich"): Die feste 30-ms-Drossel erneuerte das Spiegelbild
        // nur in jedem zweiten Bild. Bewegt sich die Kamera (Scrollen), steht
        // das Spiegelbild ein Bild lang am alten Ort, waehrend die Szene
        // weiterzieht — es zittert gegen die Objekte. Jetzt spiegelt auch das
        // Handy in JEDEM Bild, solange die Szene fluessig laeuft; erst wenn die
        // Bildzeit dauerhaft schlecht wird, faellt es auf die Drossel zurueck
        // (schwere Szenen bleiben bedienbar, s. spiegelGedrosselt).
        const mobil = istMobilesGeraet();
        let letzterAufruf = 0;
        let bildzeit = 16;
        let gedrosselt = false;
        reflector.onBeforeRender = (...args: TODOAny[]) => {
            // Aufwaerm-Bild: altes Spiegelbild stehen lassen, auch die Bildzeit-
            // Messung nicht verfaelschen (s. MirrorHost.spiegelAusgesetzt)
            if (host.spiegelAusgesetzt) return;
            const now = typeof performance !== "undefined" ? performance.now() : Date.now();
            if (letzterAufruf > 0) {
                const dt = now - letzterAufruf;
                // Mehrfach-Draws im selben Bild (< 4 ms) und Pausen (> 200 ms,
                // Tab im Hintergrund) verfaelschen den Schnitt nicht.
                if (dt >= 4 && dt <= 200) bildzeit += (dt - bildzeit) * 0.1;
            }
            letzterAufruf = now;
            if (mobil) gedrosselt = spiegelGedrosselt(gedrosselt, bildzeit);
            if (
                spiegelFaellig({
                    bildNr: host.spiegelBildNr,
                    letztesBild: rig.letztesBild,
                    seitLetztemMs: now - rig.lastRender,
                    mobil,
                    gedrosselt,
                })
            ) {
                rig.lastRender = now;
                rig.letztesBild = host.spiegelBildNr;
                // Der Spiegel-Durchlauf loescht mit der Loeschfarbe des Renderers.
                // Im VIEWER ist die durchsichtig (Alpha 0 → im Spiegel reines
                // Schwarz), die Hintergrundfarbe liefert dort das CSS dahinter.
                // Der Auslauf blendet aber auf die HINTERGRUNDFARBE aus — ein
                // ausgeblendetes Ende stand so als grauer Klotz mit harter Kante
                // im schwarzen Spiegel. Fuer diesen Durchlauf deshalb deckend mit
                // der Hintergrundfarbe loeschen (im Editor ist das ohnehin so).
                // Editor-Hilfszeichen haengen in der Hauptszene (gelber Zielpunkt-
                // Marker der Lichter, roter Drehpunkt) und wurden MITGESPIEGELT —
                // Jans Fund 20.09.2026: zwei Marker uebereinander. Fuer den Spiegel-
                // Durchlauf ausblenden, danach den alten Zustand zurueck.
                const zeichen = (host.spiegelEditorZeichen?.() ?? []).filter((z) => z.visible);
                for (const z of zeichen) z.visible = false;
                // EINBLENDENDE Objekte (Deckkraft < 100) schreiben im Hauptbild
                // bewusst keine Tiefe (renderLayer: depthWrite = false beim Faden).
                // Der Auslauf liest die Hoehe aber aus der TIEFE des Spiegel-
                // Durchlaufs — ohne Tiefe galt ein einblendender Zaehler als „kein
                // Objekt" und wurde nie ausgeblendet (Jans Fund 21.09.2026: Zahlen
                // ueber den Balken blieben im Spiegel stehen; gemessen dw=false bei
                // Deckkraft 0,05–0,97). Nur fuer den Spiegel-Durchlauf Tiefe
                // schreiben lassen — nur opak-faehige Materialien (alphaNeeded ===
                // false), echte Alpha-Flaechen (Bilder mit Ausschnitt) bleiben.
                const tiefeGeliehen: THREE.Material[] = [];
                host.webGlScene?.traverse((o) => {
                    const mesh = o as THREE.Mesh;
                    if (!mesh.isMesh || !mesh.visible) return;
                    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                    for (const m of mats) {
                        if (
                            m &&
                            m.depthWrite === false &&
                            (m.userData as { alphaNeeded?: boolean } | undefined)?.alphaNeeded === false &&
                            m.opacity > 0.02
                        ) {
                            m.depthWrite = true;
                            tiefeGeliehen.push(m);
                        }
                    }
                });
                const tiefeZurueck = () => {
                    for (const m of tiefeGeliehen) m.depthWrite = false;
                };
                const rend = args[0] as THREE.WebGLRenderer | undefined;
                const zielClear = host.spiegelAuslaufZiel;
                if (rend && zielClear) {
                    const alteFarbe = rend.getClearColor(new THREE.Color());
                    const altesAlpha = rend.getClearAlpha();
                    rend.setClearColor(new THREE.Color(zielClear.r, zielClear.g, zielClear.b), 1);
                    try {
                        origOnBeforeRender.apply(reflector, args);
                    } finally {
                        rend.setClearColor(alteFarbe, altesAlpha);
                        for (const z of zeichen) z.visible = true;
                        tiefeZurueck();
                    }
                } else {
                    try {
                        origOnBeforeRender.apply(reflector, args);
                    } finally {
                        for (const z of zeichen) z.visible = true;
                        tiefeZurueck();
                    }
                }
                // Auslauf: inverse Sicht-Projektion der VIRTUELLEN Kamera (three
                // verbiegt deren Projektion fuer das schraege Near-Clipping —
                // deshalb hier frisch invertieren) und die Spiegel-Ebene in Welt.
                const vCam = (reflector as TODOAny).camera as THREE.PerspectiveCamera | undefined;
                if (vCam && rmat.uniforms?.uSpiegelInvVP) {
                    const inv = rmat.uniforms.uSpiegelInvVP.value as THREE.Matrix4;
                    inv.copy(vCam.projectionMatrix).invert().premultiply(vCam.matrixWorld);
                    const n = new THREE.Vector3(0, 0, 1).transformDirection(reflector.matrixWorld);
                    const p0 = new THREE.Vector3().setFromMatrixPosition(reflector.matrixWorld);
                    (rmat.uniforms.uSpiegelEbene.value as THREE.Vector4).set(n.x, n.y, n.z, -n.dot(p0));
                }
                // UNSCHARF: das frische Spiegelbild durch den Gauss schicken
                if (rig.weich && rend && reflRt?.texture && (rig.sigmaPx ?? 0) > 0) {
                    rmat.uniforms.tWeich.value = rig.weich.render(rend, {
                        quelle: reflRt.texture,
                        tiefe,
                        invVP: rmat.uniforms.uSpiegelInvVP.value,
                        ebene: rmat.uniforms.uSpiegelEbene.value,
                        auslauf: rmat.uniforms.uAuslauf.value,
                        sigmaPx: rig.sigmaPx ?? 0,
                    });
                    rmat.uniforms.uWeichAn.value = 1;
                } else {
                    rmat.uniforms.uWeichAn.value = 0;
                }
            }
        };
        host.mirrorRigs[id] = rig;
    }

    // apply live tuning (tint + strength/mix + blur) each frame
    for (const id of Object.keys(host.mirrorRigs)) {
        const rig = host.mirrorRigs[id];
        const params = host.mirrorParams[id];
        const rmat = rig.reflector.material as TODOAny;
        // Selbstheilung: traegt der Reflector nicht mehr seinen gepatchten
        // Shader (uOpacity fehlt — irgendwer hat das Material ersetzt),
        // Rig abbauen; der Neubau folgt im naechsten updateMirrors-Lauf.
        if (!rmat?.uniforms?.uOpacity) {
            teardownMirror(host, id);
            continue;
        }
        // tint
        if (params?.color && rmat.uniforms?.color) {
            rmat.uniforms.color.value = colorToThree(params.color);
        }
        // strength: blend the reflection toward the base colour IN THE SHADER
        const strength = Math.max(0, Math.min(1, params?.strength ?? 1));
        if (rmat.uniforms?.uOpacity) rmat.uniforms.uOpacity.value = strength;
        if (params?.mixColor && rmat.uniforms?.uMixColor) {
            rmat.uniforms.uMixColor.value = colorToThree(params.mixColor);
        }
        // blur → Gauss-Breite in Textur-Pixeln (s. spiegel-weichzeichner.ts)
        const blur = Math.max(0, Math.min(1, params?.blur ?? 0));
        rig.sigmaPx = weichSigmaBezug(blur) * (rig.res / 512);
        if (rmat.uniforms?.uAuslauf) {
            rmat.uniforms.uAuslauf.value = spiegelAuslaufLaenge(params?.fade, host.spiegelBildHoehe ?? 700);
            if (rmat.uniforms.uFresnel) rmat.uniforms.uFresnel.value = spiegelBlickwinkel(params?.fresnel);
            const zielF = host.spiegelAuslaufZiel;
            if (zielF) (rmat.uniforms.uAuslaufZiel.value as THREE.Vector3).set(zielF.r, zielF.g, zielF.b);
        }
        if (rmat.uniforms?.uBlurStep) rmat.uniforms.uBlurStep.value = spiegelNachglaettung(rig.breite);
        rmat.transparent = false;
        if (rig.mesh) rig.mesh.visible = false;
    }
}

export function teardownMirror(host: MirrorHost, id: string) {
    const rig = host.mirrorRigs[id];
    if (!rig) return;
    if (rig.reflector?.parent) {
        rig.reflector.parent.remove(rig.reflector);
    }
    // disposes the render target + reflector material (NOT the shared geometry)
    try {
        rig.reflector?.dispose?.();
    } catch {
        /* ignore */
    }
    // the base plane was hidden while the mirror was on — show it again
    if (rig.mesh) rig.mesh.visible = true;
    rig.weich?.dispose();
    delete host.mirrorRigs[id];
}
